import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useConversationsListQuery } from '../hooks/useConversationsListQuery';
import { useConversationDetailQuery } from '../hooks/useConversationDetailQuery';
import { invalidateMessagingCaches } from '../lib/messagingQueryCache';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { getAnnouncementImageUrl } from '../utils/imageUrl';
import FoundObjectMessagesWorkspace from '../components/FoundObjectMessagesWorkspace';
import { getFoundObjectIdFromConversation } from '../utils/foundObjectConversation';
import {
  ConversationAvatar,
  getConversationListButtonSx,
} from '../components/conversation/ConversationChatUi';

function firstImageUrl(images?: string[] | null): string | null {
  const p = images?.find((x) => x && String(x).trim());
  return p ? getAnnouncementImageUrl(p) : null;
}

const MessagesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const conversationParam = searchParams.get('conversation');
  const announcementParam = searchParams.get('announcement');
  const foundObjectParam = searchParams.get('found_object');

  const selectedId = conversationParam ? Number.parseInt(conversationParam, 10) : null;
  const [startError, setStartError] = useState<string | null>(null);
  const [startingConversation, setStartingConversation] = useState(false);
  const openingRef = useRef(false);
  const [listFilter, setListFilter] = useState<'pending' | 'unread' | 'all'>('all');
  const [sortOrder, setSortOrder] = useState<'recent' | 'oldest'>('recent');
  const [searchText, setSearchText] = useState('');

  const { data: convData, isLoading: loadingList } = useConversationsListQuery({ poll: true });

  const {
    data: detailData,
    isLoading: loadingDetail,
    error: detailError,
  } = useConversationDetailQuery({
    conversationId: selectedId,
    poll: true,
  });

  const selectedFoundObjectId = foundObjectParam ? Number.parseInt(foundObjectParam, 10) : null;

  useEffect(() => {
    const aid = announcementParam ? Number.parseInt(announcementParam, 10) : null;
    if (!aid || Number.isNaN(aid) || conversationParam) return;
    if (openingRef.current) return;
    openingRef.current = true;
    setStartingConversation(true);
    setStartError(null);
    apiService
      .startConversation(aid)
      .then((data) => {
        openingRef.current = false;
        setStartingConversation(false);
        setSearchParams({ conversation: String(data.conversation_id) });
        void invalidateMessagingCaches(queryClient);
        queryClient.invalidateQueries({ queryKey: ['conversation', data.conversation_id] });
      })
      .catch((err: any) => {
        openingRef.current = false;
        setStartingConversation(false);
        setStartError(err?.response?.data?.error || 'Impossible de démarrer la conversation.');
      });
  }, [announcementParam, conversationParam, setSearchParams, queryClient]);

  const conversations = convData?.conversations ?? [];
  const totalUnread = convData?.total_unread ?? 0;
  const pendingCount = conversations.filter((c: any) => c.last_message && !c.last_message.is_from_me).length;
  const unreadCount = conversations.filter((c: any) => (c.unread_count ?? 0) > 0).length;

  const filteredConversations = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    let result = [...conversations];

    if (listFilter === 'pending') {
      result = result.filter((c: any) => c.last_message && !c.last_message.is_from_me);
    } else if (listFilter === 'unread') {
      result = result.filter((c: any) => (c.unread_count ?? 0) > 0);
    }

    if (normalizedSearch) {
      result = result.filter((c: any) => {
        const fullName = `${c.other_user?.first_name ?? ''} ${c.other_user?.last_name ?? ''}`.toLowerCase();
        const title = String(c.announcement?.title ?? '').toLowerCase();
        const preview = String(c.last_message?.content ?? '').toLowerCase();
        return (
          fullName.includes(normalizedSearch) ||
          title.includes(normalizedSearch) ||
          preview.includes(normalizedSearch)
        );
      });
    }

    result.sort((a: any, b: any) => {
      const ta = new Date(a.updated_at ?? 0).getTime();
      const tb = new Date(b.updated_at ?? 0).getTime();
      return sortOrder === 'recent' ? tb - ta : ta - tb;
    });

    return result;
  }, [conversations, listFilter, searchText, sortOrder]);

  const inferredFoundObjectIdFromConversation = useMemo(() => {
    const attrs = detailData?.conversation?.announcement?.object_attributes;
    const fromAttrs = Number(attrs?.found_object_id);
    if (Number.isFinite(fromAttrs) && fromAttrs > 0) return fromAttrs;

    const ref = String(detailData?.conversation?.announcement?.reference_code ?? '');
    const match = ref.match(/^FO-CONTACT-(\d+)$/);
    if (match?.[1]) return Number.parseInt(match[1], 10);
    return null;
  }, [detailData]);

  const activeFoundObjectId = selectedFoundObjectId ?? inferredFoundObjectIdFromConversation;

  /** Complète l’URL avec `found_object` une fois la conversation chargée (liens directs `?conversation=`). */
  useEffect(() => {
    if (!selectedId || Number.isNaN(selectedId) || foundObjectParam) return;
    const inferred = inferredFoundObjectIdFromConversation;
    if (inferred != null && inferred > 0) {
      setSearchParams(
        { conversation: String(selectedId), found_object: String(inferred) },
        { replace: true }
      );
    }
  }, [selectedId, foundObjectParam, inferredFoundObjectIdFromConversation, setSearchParams]);

  const isObjectMessagingView = Boolean(selectedId || activeFoundObjectId || announcementParam);

  const handleSelect = (id: number) => {
    const c = conversations.find((x: any) => x.id === id);
    const foId = getFoundObjectIdFromConversation(c);
    const nextParams: Record<string, string> = { conversation: String(id) };
    if (foId != null) {
      nextParams.found_object = String(foId);
    }
    setSearchParams(nextParams);
  };

  const handleWorkspaceConversation = (id: number | null) => {
    if (id == null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('conversation');
        return next;
      });
      return;
    }
    handleSelect(id);
  };

  const closeMessagingDetail = () => {
    setSearchParams({});
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        minHeight: { xs: 360, md: 480 },
        maxWidth: '100%',
      }}
    >
      {!isObjectMessagingView && (
      <Card sx={{ width: '100%', maxWidth: '100%', maxHeight: { xs: '42vh', md: '78vh' }, overflow: 'auto' }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Messagerie
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {totalUnread > 0 ? `${totalUnread} non lu(s)` : 'À jour'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5, rowGap: 1 }}>
            <Chip
              label={`En attente de réponse ${pendingCount}`}
              color={listFilter === 'pending' ? 'warning' : 'default'}
              variant={listFilter === 'pending' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('pending')}
              clickable
            />
            <Chip
              label={`Non lus ${unreadCount}`}
              color={listFilter === 'unread' ? 'error' : 'default'}
              variant={listFilter === 'unread' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('unread')}
              clickable
            />
            <Chip
              label={`Tous ${conversations.length}`}
              color={listFilter === 'all' ? 'primary' : 'default'}
              variant={listFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => setListFilter('all')}
              clickable
            />
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Trier par :
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={sortOrder}
              onChange={(_, value) => value && setSortOrder(value)}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              <ToggleButton value="recent">Plus récent</ToggleButton>
              <ToggleButton value="oldest">Plus ancien</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher dans les messages..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Box>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Aucun message pour ce filtre.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 1 }}>
            {filteredConversations.map((c: any) => (
              <ListItem key={c.id} disablePadding sx={{ mb: 0.75 }}>
                <ListItemButton
                  selected={selectedId === c.id}
                  onClick={() => handleSelect(c.id)}
                  sx={{
                    borderRadius: 2,
                    alignItems: 'flex-start',
                    ...getConversationListButtonSx(c.id, selectedId === c.id),
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!c.unread_count || c.unread_count === 0}
                      overlap="circular"
                    >
                      <ConversationAvatar
                        conversationId={c.id}
                        imageSrc={firstImageUrl(c.announcement?.images)}
                        initial={c.other_user?.first_name}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {c.other_user ? `${c.other_user.first_name} ${c.other_user.last_name}` : 'Utilisateur'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {c.updated_at ? new Date(c.updated_at).toLocaleDateString('fr-FR') : ''}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {c.announcement?.title || 'Annonce'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {c.last_message?.content || '…'}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Card>
      )}

      {isObjectMessagingView && activeFoundObjectId != null && activeFoundObjectId > 0 && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal - 1,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <FoundObjectMessagesWorkspace
              variant="fullscreen"
              foundObjectId={activeFoundObjectId}
              selectedConversationId={selectedId}
              onSelectConversation={handleWorkspaceConversation}
              onClose={closeMessagingDetail}
              onRequestEditFoundObject={() => {
                navigate(`/found-objects?open=${activeFoundObjectId}`);
              }}
              announcementFlow={!!announcementParam}
              conversationPresentInUrl={!!conversationParam}
              startingConversation={startingConversation}
              startError={startError}
              onDismissStartError={() => setStartError(null)}
              onHandoverConfirmed={closeMessagingDetail}
            />
          </Box>
        </Box>
      )}
      {isObjectMessagingView &&
        (!activeFoundObjectId || activeFoundObjectId <= 0) &&
        announcementParam &&
        !conversationParam && (
          <Card sx={{ p: 3, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={28} />
              <Typography>Préparation de la conversation…</Typography>
            </Box>
          </Card>
        )}
    </Box>
  );
};

export default MessagesPage;
